/**
 * Measure-click wiring for VolumeViewport.
 *
 * Owns the per-element handler that MeasureClickTool's preMouseDownCallback
 * invokes. First click = fresh nnInteractive session (reset:true). Shift+click
 * = positive refinement, Ctrl/Meta+click = negative refinement (both reuse the
 * same session_id and append to the running interactions[] list — same shape
 * the labeling endpoint uses).
 *
 * After each successful response, the segmentation contour is rendered as a
 * Cornerstone annotation via createAnnotationsFromNnResponse — the same
 * helper RegionGrowTool uses for labeling. Old contour UIDs from the
 * previous click are removed first so the overlay always reflects the latest
 * segmentation. When measureMode clears (Done / Cancel), all contour
 * annotations are removed.
 */
import { useEffect, useRef, RefObject } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import axios from "axios";

import { setMeasureClickHandler } from "../tools/MeasureClickTool";
import { runCommand } from "../lib/commands";
import {
  measureModeAtom,
  measureLoadingAtom,
  measureErrorAtom,
  measureResultAtom,
  measureSessionAtom,
  measureClickPinAtom,
  measureSegmentationResultAtom,
  type MeasureSession,
} from "../atoms/measureAtoms";
import { useToken } from "../context/AuthContext";
import { getApiBaseUrl } from "../config/api";
import { getCornerstone, getCornerstoneTools } from "../utils/dicomLoader";
import { createAnnotationsFromNnResponse } from "../tools/nnInteractiveSession";

interface MeasureApiResponse {
  session_id?: string;
  contours?: Record<string, number[][][]>;
  volume_mm3: number;
  hull_stats?: { axis_1_mm: number; axis_2_mm: number; axis_3_mm: number };
  max_diameter_mm?: number;
}

function buildAxesCm(d: MeasureApiResponse): [number, number, number] {
  if (d.hull_stats) {
    return [d.hull_stats.axis_1_mm / 10, d.hull_stats.axis_2_mm / 10, d.hull_stats.axis_3_mm / 10];
  }
  const long = (d.max_diameter_mm ?? 0) / 10;
  return [long, long * 0.7, long * 0.5];
}

/** Remove a list of Cornerstone annotation UIDs without throwing if any are
 * already gone. Used to clear the previous contour before re-rendering and
 * on session end. */
function removeAnnotations(uids: string[]): void {
  if (uids.length === 0) return;
  try {
    const cs = getCornerstoneTools();
    const remove = cs.annotation?.state?.removeAnnotation;
    if (typeof remove === "function") {
      for (const uid of uids) {
        try {
          remove(uid);
        } catch {
          /* annotation already removed */
        }
      }
    }
  } catch {
    /* cornerstone not initialized */
  }
}

export function useMeasureClickWiring(
  viewportRef: RefObject<HTMLElement | null>,
  seriesId: string | undefined | null,
): void {
  const measureMode = useAtomValue(measureModeAtom);
  const token = useToken();
  const setMeasureLoading = useSetAtom(measureLoadingAtom);
  const setMeasureError = useSetAtom(measureErrorAtom);
  const setMeasureResult = useSetAtom(measureResultAtom);
  const setMeasureClickPin = useSetAtom(measureClickPinAtom);
  const setMeasureSegResult = useSetAtom(measureSegmentationResultAtom);
  const [session, setSession] = useAtom(measureSessionAtom);

  // Mirror live state into refs so the click handler reads the CURRENT
  // values at click time without triggering re-registration. Re-registering
  // on every state change would create a brief window where a follow-up
  // click could miss the new handler.
  const sessionRef = useRef(session);
  const measureModeRef = useRef(measureMode);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);
  useEffect(() => {
    measureModeRef.current = measureMode;
  }, [measureMode]);

  // Stable boolean: true when measureMode exists. Toggle from false→true
  // arms the tool; true→false disarms. fieldEnd updates after a successful
  // measurement do NOT toggle this, so the handler stays registered for
  // refinement clicks.
  const measureModeActive = !!measureMode;

  // Clean up rendered contours + click pin when measureMode is cleared
  // (Done / Cancel / unmount). The session atom holds the UIDs so any
  // viewport that owns the current session cleans them up.
  useEffect(() => {
    if (measureMode) return;
    if (session) {
      removeAnnotations(session.contourUIDs);
      setSession(null);
    }
    setMeasureClickPin(null);
    setMeasureSegResult(null);
  }, [measureMode, session, setSession, setMeasureClickPin, setMeasureSegResult]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || !measureModeActive || !seriesId) {
      if (el) setMeasureClickHandler(el, null);
      return;
    }
    let cancelled = false;
    setMeasureClickHandler(el, async (voxelZYX, modifiers, position) => {
      const liveMode = measureModeRef.current;
      if (!liveMode) return;
      const isRefine = modifiers.shift || modifiers.ctrl;
      const label: "positive" | "negative" = modifiers.ctrl ? "negative" : "positive";

      // Drop a click pin at the exact click point so the user has clear
      // visual confirmation, regardless of how small the contour is.
      setMeasureClickPin({
        pageX: position.pageX,
        pageY: position.pageY,
        volumeMl: null,
        seriesUid: seriesId,
        sliceIndex: voxelZYX[0],
      });

      // Build the request: refinement reuses session_id and appends to
      // interactions[]; first click resets.
      const prevSession = isRefine ? sessionRef.current : null;
      const nextInteractions: MeasureSession["interactions"] = [
        ...(prevSession?.interactions ?? []),
        { type: "point", coordinates: voxelZYX, label },
      ];

      setMeasureLoading(true);
      setMeasureError(null);
      try {
        const resp = await axios.post<MeasureApiResponse>(
          `${getApiBaseUrl()}/api/measure/lesion-segment`,
          {
            job_id: liveMode.jobId,
            series_uid: seriesId,
            interactions: nextInteractions,
            session_id: prevSession?.sessionId ?? null,
            reset: !prevSession,
          },
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            timeout: 60_000,
          },
        );
        if (cancelled) return;
        const d = resp.data;

        // Render contour overlay — reuse the labeling helper.
        const oldUIDs = prevSession?.contourUIDs ?? [];
        let newUIDs: string[] = [];
        try {
          const cs = getCornerstone();
          const csTools = getCornerstoneTools();
          const enabled = cs.getEnabledElement(el as unknown as HTMLDivElement);
          const viewport = enabled?.viewport as { getImageData?: () => unknown } | undefined;
          const imageData = viewport?.getImageData?.();
          if (imageData && d.contours && Object.keys(d.contours).length > 0) {
            // CachedContext: createAnnotationsFromNnResponse only reads
            // viewport, element, and imageData — the HU fields are filler.
            const ctx = {
              viewport: enabled!.viewport,
              element: el,
              imageData,
              scalarData: null,
              cols: 0,
              rows: 0,
              effectiveMin: 0,
              effectiveMax: 0,
              maxRadiusPx: 0,
              smoothingIter: 0,
            };
            // Dynamic-import the response shape contract via duck-typing:
            // createAnnotationsFromNnResponse expects { contours, ... }.
            // Cast through unknown — labeling's NnInteractiveResponse has
            // extra fields we don't populate.
            newUIDs = createAnnotationsFromNnResponse(
              d as unknown as Parameters<typeof createAnnotationsFromNnResponse>[0],
              ctx as unknown as Parameters<typeof createAnnotationsFromNnResponse>[1],
              1,
            );
            // Clear the previous contour AFTER drawing the new one so the
            // overlay never blinks empty.
            removeAnnotations(oldUIDs);
            try {
              csTools.utilities?.triggerAnnotationRender?.(el as unknown as HTMLDivElement);
            } catch {
              /* noop */
            }
          }
        } catch (e) {
          console.warn("[measure] contour overlay failed (non-fatal)", e);
          newUIDs = oldUIDs; // keep old UIDs in session so cleanup can find them
        }

        if (cancelled) {
          removeAnnotations(newUIDs);
          return;
        }

        setSession({
          sessionId: d.session_id ?? prevSession?.sessionId ?? "",
          interactions: nextInteractions,
          contourUIDs: newUIDs,
        });

        const axesCm = buildAxesCm(d);
        const volumeMl = d.volume_mm3 / 1000;
        setMeasureResult({
          findingId: liveMode.findingId,
          volumeMl,
          axesCm,
          seriesUid: seriesId,
          sliceIndex: voxelZYX[0],
        });
        // Update the click pin with the resolved volume. Capture every slice
        // index the response's contours cover so the pin can render on every
        // slice the mask is visible on, not just the click slice.
        const maskSliceIndices = d.contours
          ? Object.keys(d.contours)
              .map((k) => parseInt(k, 10))
              .filter((n) => Number.isFinite(n))
          : [];
        setMeasureClickPin({
          pageX: position.pageX,
          pageY: position.pageY,
          volumeMl,
          seriesUid: seriesId,
          sliceIndex: voxelZYX[0],
          maskSliceIndices,
        });

        // Populate the 3D viewer atom: pull plane geometry from the clicked
        // viewport's DICOM metadata (same pattern as labeling's nnInteractive
        // result builder). Only axial — the measure flow doesn't fetch
        // coronal/sagittal companions.
        try {
          const cs = getCornerstone();
          const enabled = cs.getEnabledElement(el as unknown as HTMLDivElement);
          const viewport = enabled?.viewport as
            | {
                getImageData?: () => { dimensions?: number[]; spacing?: number[] };
                getImageIds?: () => string[];
              }
            | undefined;
          const imageData = viewport?.getImageData?.();
          const allIds: string[] = viewport?.getImageIds?.() ?? [];
          if (imageData && allIds.length >= 2 && d.contours) {
            const dims = imageData.dimensions ?? [0, 0];
            const sp = imageData.spacing ?? [1, 1, 1];
            let origin: [number, number, number] = [0, 0, 0];
            let rowCos: [number, number, number] = [1, 0, 0];
            let colCos: [number, number, number] = [0, 1, 0];
            let sliceDir: [number, number, number] = [0, 0, 1];
            // Real slice thickness in mm — derived from the distance between
            // imagePositionPatient of consecutive slices. Stack viewports
            // don't expose this via imageData.spacing[2] (they report ~0
            // because there is no 3D depth in a stack), so we compute it.
            let sliceSp = 0;
            try {
              const ipm0 = cs.metaData.get("imagePlaneModule", allIds[0]);
              const ipm1 = cs.metaData.get("imagePlaneModule", allIds[1]);
              if (ipm0?.imagePositionPatient) origin = ipm0.imagePositionPatient;
              if (ipm0?.rowCosines) rowCos = ipm0.rowCosines;
              if (ipm0?.columnCosines) colCos = ipm0.columnCosines;
              if (ipm0?.imagePositionPatient && ipm1?.imagePositionPatient) {
                const dx = ipm1.imagePositionPatient[0] - ipm0.imagePositionPatient[0];
                const dy = ipm1.imagePositionPatient[1] - ipm0.imagePositionPatient[1];
                const dz = ipm1.imagePositionPatient[2] - ipm0.imagePositionPatient[2];
                const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
                if (len > 0.01) {
                  sliceDir = [dx / len, dy / len, dz / len];
                  sliceSp = len;
                }
              }
            } catch {
              /* fall back to defaults */
            }
            // planeContourDataToWorldPolylines expects spacing in [z, y, x]
            // order (slice, row, col). Cornerstone's imageData.spacing is
            // [x, y, z] for volume viewports, but stack viewports report a
            // bogus ~0 z-spacing — so we use the real slice distance derived
            // from imagePositionPatient instead. sp[1]/sp[0] still give row
            // (y) and column (x) pixel spacing.
            const spacingZYX: [number, number, number] = [sliceSp || sp[2] || 1, sp[1], sp[0]];
            setMeasureSegResult({
              contours: d.contours,
              spacing: spacingZYX,
              dimensions: [allIds.length, dims[1] ?? dims[0], dims[0]],
              origin,
              rowCosines: rowCos,
              columnCosines: colCos,
              sliceDirection: sliceDir,
              volumeMm3: d.volume_mm3,
            });
          }
        } catch (e) {
          console.warn("[measure] 3D atom populate failed (non-fatal)", e);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Measurement failed — try again";
        setMeasureError(message);
        runCommand("exitMeasureMode", {});
      } finally {
        if (!cancelled) setMeasureLoading(false);
      }
    });
    return () => {
      cancelled = true;
      setMeasureClickHandler(el, null);
    };
    // measureMode (full object) and session are read via refs at click
    // time. We only re-run the effect on existence toggles + identity
    // changes that REQUIRE re-registration (different element, different
    // series, different token).
  }, [
    viewportRef,
    measureModeActive,
    seriesId,
    token,
    setMeasureLoading,
    setMeasureError,
    setMeasureResult,
    setSession,
    setMeasureClickPin,
    setMeasureSegResult,
  ]);
}
